import { useLayoutEffect, useRef } from 'react';
import type { RefObject } from 'react';

/**
 * Замена `AnimatedSize` из Flutter.
 *
 * Во Flutter виджет сам знает размер ребёнка на каждом кадре и плавно ведёт
 * к нему собственную коробку. В вебе размер приходится измерять: внутренний
 * элемент остаётся «по содержимому», `ResizeObserver` ловит его изменения,
 * а внешнему проставляется точная высота с переходом. Это единственный
 * способ анимировать `height: auto` во всех браузерах, а не только
 * в самых свежих.
 *
 * Хук лежит рядом с экраном чата, а не внутри одного из его файлов: то же
 * `AnimatedSize` встречается в трёх местах экрана (заголовок ответа,
 * пузырь сообщения и сетка подсказок), и копировать замер трижды нельзя.
 *
 * Обрезка (`clipBehavior`) и выравнивание (`alignment`) задаются в CSS
 * вызывающего: у Dart-версий они разные.
 *
 * Едет только высота. Ширину во всех трёх местах определяет содержимое,
 * а измерять её и тут же проставлять наружному элементу нельзя: внутренний
 * элемент занимает всю его ширину, замер начал бы кормить сам себя.
 * Ширина поэтому меняется мгновенно — как и во Flutter по горизонтали
 * почти нечему ехать: два из трёх мест имеют фиксированную ширину.
 */

/** Замера ещё не было — первый размер ставится без перехода. */
const NO_MEASUREMENT = -1;

export interface AnimatedSizeOptions {
  /** `duration` из Dart, мс. */
  readonly durationMs: number;
  /** `curve`; у `AnimatedSize` по умолчанию `Curves.linear`. */
  readonly curve?: string;
}

export interface AnimatedSizeRefs {
  /** Коробка, которая едет: на неё уходит измеренный размер. */
  readonly outerRef: RefObject<HTMLDivElement | null>;
  /** Содержимое: его размер и измеряется. */
  readonly innerRef: RefObject<HTMLDivElement | null>;
}

export function useAnimatedSize(options: AnimatedSizeOptions): AnimatedSizeRefs {
  const { durationMs, curve = 'linear' } = options;
  const outerRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (outer === null || inner === null) return undefined;
    // Без ResizeObserver (старый движок, jsdom в тестах) размер просто
    // не анимируется — содержимое при этом видно целиком, как и должно.
    if (typeof ResizeObserver === 'undefined') return undefined;

    let previousHeight = NO_MEASUREMENT;

    /*
      Стили ставятся через `setProperty`, а не присваиванием полей: элементы
      пришли из ref, и менять их поля на месте в этом проекте нельзя.
    */
    const apply = (): void => {
      const { height } = inner.getBoundingClientRect();
      const transition =
        previousHeight === NO_MEASUREMENT ? 'none' : `height ${durationMs}ms ${curve}`;
      outer.style.setProperty('transition', transition);
      outer.style.setProperty('height', `${height}px`);
      previousHeight = height;
    };

    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(inner);

    return () => {
      observer.disconnect();
      outer.style.removeProperty('transition');
      outer.style.removeProperty('height');
    };
  }, [durationMs, curve]);

  return { outerRef, innerRef };
}
